/**
 * Manim代码生成相关API
 * 包含代码生成和修复功能
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import {
  convertImageToBase64,
  requestManager,
  getCurrentUser,
  getCustomClientSlug,
  requestModelByModeConfig,
} from "./utils";

/**
 * 提取代码部分
 * 从AI响应中提取Manim代码
 * @param {string} response - AI的完整响应
 * @returns {Promise<string>} 提取的Manim代码
 */
export const extractManimCode = async (response, mode = "manim") => {
  const importStatement = mode === "manimgx" ? "from manimgx import *" : "from manim import *";
  try {
    // 查找代码块 - 在```python 和 ``` 之间
    const codeBlockRegex = /```(?:python)?([\s\S]*?)```/g;
    const matches = [...response.matchAll(codeBlockRegex)];

    let code = "";

    if (matches.length > 0) {
      // 找到最有可能是完整代码的代码块
      // 通常是最长的包含"from manim import"的代码块
      let bestMatch = "";

      for (const match of matches) {
        const codeBlock = match[1].trim();

        // 如果代码块包含from manim import，优先选择
        if (
          codeBlock.includes("from manim import") ||
          codeBlock.includes("import manim") ||
          codeBlock.includes("from manimgx import") ||
          codeBlock.includes("import manimgx")
        ) {
          // 如果找到的代码块比当前最佳匹配长，或当前没有最佳匹配，则更新
          if (codeBlock.length > bestMatch.length || bestMatch === "") {
            bestMatch = codeBlock;
          }
        }
      }

      // 如果没有找到含有manim导入的代码块，就选择最长的代码块
      if (bestMatch === "") {
        bestMatch = matches.reduce((longest, match) => {
          return match[1].trim().length > longest.length
            ? match[1].trim()
            : longest;
        }, "");
      }

      Logger.debug("成功提取代码块", {
        matchesCount: matches.length,
        bestMatchLength: bestMatch.length,
      });

      code = bestMatch;
    } else {
      // 如果没有找到代码块，尝试查找从"from manim/manimgx import"开始的部分
      let importIndex = response.indexOf("from manim import");
      if (importIndex === -1) importIndex = response.indexOf("from manimgx import");
      if (importIndex === -1) importIndex = response.indexOf("import manim");
      if (importIndex === -1) importIndex = response.indexOf("import manimgx");

      if (importIndex !== -1) {
        Logger.debug("通过import语句提取代码");
        code = response.substring(importIndex).trim();
      } else {
        // 实在找不到代码，查找是否有class定义
        const classIndex = response.indexOf("class ");
        if (classIndex !== -1 && classIndex < 500) {
          // 只在响应前部分查找类定义
          // 拼接一个导入语句
          Logger.debug("通过class定义提取代码，添加缺失的导入语句");
          code =
            importStatement + "\n\n" + response.substring(classIndex).trim();
        } else {
          // 如果什么都找不到，返回整个响应内容，并在日志中警告
          Logger.warn("未能找到明确的代码块，将整个响应作为代码处理");
          code = response;
        }
      }
    }

    // 清理代码：移除可能的注释和指示
    code = cleanupCode(code);

    // 确保代码包含一个主Scene类
    return ensureMainSceneClass(code, mode);
  } catch (error) {
    Logger.error("提取代码失败", error);
    return response; // 失败时返回原始响应
  }
};

/**
 * 清理提取的代码，移除可能的指示性文字
 * @param {string} code - 提取的代码
 * @returns {string} 清理后的代码
 */
const cleanupCode = (code) => {
  // 移除代码开头的非代码指示文字
  let cleanCode = code;

  // 移除"以下是修复后的代码"等提示性文字
  const codeStartPatterns = [
    "以下是修复后的代码：",
    "以下是修复后的完整代码：",
    "下面是修复后的代码：",
    "修复后的代码：",
    "修复后的完整代码：",
    "以下是完整的修复代码：",
    "Here is the fixed code:",
  ];

  for (const pattern of codeStartPatterns) {
    if (cleanCode.includes(pattern)) {
      const index = cleanCode.indexOf(pattern);
      cleanCode = cleanCode.substring(index + pattern.length).trim();
    }
  }

  // 移除开头的空行和注释行
  const lines = cleanCode.split("\n");
  let firstCodeLineIndex = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line !== "" && !line.startsWith("#")) {
      firstCodeLineIndex = i;
      break;
    }
  }

  // 只保留从第一行有效代码开始的内容
  cleanCode = lines.slice(firstCodeLineIndex).join("\n");

  return cleanCode;
};

/**
 * 确保代码包含一个主Scene类，并注入运行时补丁来处理常见错误。
 * @param {string} code - 提取的Manim代码
 * @returns {string} 修正并打上补丁后的代码
 */
const ensureMainSceneClass = (code, mode = "manim") => {
  const importStatement = mode === "manimgx" ? "from manimgx import *" : "from manim import *";
  let finalCode = code;

  // 检查是否存在继承自Scene的类
  const sceneClassRegex = /class\s+\w+\s*\(\s*(?:ThreeD)?Scene\s*\)/;
  if (!sceneClassRegex.test(finalCode)) {
    Logger.warn("未找到继承自Scene或ThreeDScene的类，将尝试修复或包裹");

    // 查找所有类定义
    const classRegex = /class\s+(\w+)(?:\(([^)]*)\))?:/g;
    const hasAnyClass = classRegex.test(finalCode);
    classRegex.lastIndex = 0; // 重置正则表达式状态

    if (hasAnyClass) {
      // 如果有类但没有继承Scene，则进行修复
      let modifiedCode = finalCode;
      let match;
      while ((match = classRegex.exec(finalCode)) !== null) {
        const className = match[1];
        const parentClass = match[2];
        if (
          !parentClass ||
          (!parentClass.includes("Scene") &&
            !parentClass.includes("ThreeDScene"))
        ) {
          const is3D = finalCode.includes("set_camera_orientation");
          const newParentClass = is3D ? "ThreeDScene" : "Scene";
          if (!parentClass) {
            modifiedCode = modifiedCode.replace(
              new RegExp(`class\\s+${className}\\s*:`),
              `class ${className}(${newParentClass}):`
            );
          } else {
            modifiedCode = modifiedCode.replace(
              new RegExp(`class\\s+${className}\\s*\\([^)]*\\):`),
              `class ${className}(${newParentClass}):`
            );
          }
        }
      }
      finalCode = modifiedCode;
    } else {
      // 如果没有任何类定义，则创建一个包装类
      Logger.warn("未找到任何类定义，创建一个默认Scene类");
      const is3D = finalCode.includes("set_camera_orientation");
      const sceneType = is3D ? "ThreeDScene" : "Scene";

      // 注意：包裹时使用原始的`code`，并且包裹模板自带import语句
      finalCode = `${importStatement}

class MainScene(${sceneType}):
    def construct(self):
        # 以下是生成的代码
${code
  .split("\n")
  .map((line) => "        " + line)
  .join("\n")}`;
    }
  }

  // 确保最终的代码有import语句（如果不是通过包裹添加的）
  if (!finalCode.includes("from manim import") && !finalCode.includes("from manimgx import") && !finalCode.includes("import manimgx") && !finalCode.includes("import manim")) {
    finalCode = importStatement + "\n\n" + finalCode;
  }

  return finalCode;
};

/**
 * 自动转义 LaTeX 文本模式 \text{...} 中的下划线为 \_ （启发式，未处理嵌套花括号的极端情况）
 * @param {string} code - 提取的代码
 * @returns {string} 转义后的代码
 */
export const escapeUnderscoreInLaTeXText = (code) => {
  try {
    return code.replace(/\\text\{([^}]*)\}/g, (match, inner) => {
      const escaped = inner.replace(/_/g, "\\_");
      return `\\text{${escaped}}`;
    });
  } catch (_) {
    return code;
  }
};

/**
 * 简单验证Manim代码的完整性
 * @param {string} code - 提取的Manim代码
 * @returns {Object} 验证结果，包含valid标志和可能的issues
 */
const validateManimCode = (code) => {
  const issues = [];

  // 检查基本导入
  if (!code.includes("from manim import") && !code.includes("from manimgx import") && !code.includes("import manimgx") && !code.includes("import manim")) {
    issues.push("缺少基本导入语句");
  }

  // 检查是2D还是3D场景
  const is3D =
    code.includes("ThreeDScene") || code.includes("set_camera_orientation");

  // 正则表达式用于检查场景类定义
  const sceneClassRegex = /class\s+\w+\s*\(\s*(?:ThreeD)?Scene\s*\)/i;

  // 检查Scene类定义
  if (!sceneClassRegex.test(code)) {
    issues.push("缺少正确的Scene或ThreeDScene类定义");
  }

  // 检查construct方法
  if (!code.includes("def construct")) {
    issues.push("缺少construct方法");
  }

  // 检查基本的self引用
  if (!code.includes("self.play") && !code.includes("self.wait")) {
    issues.push("缺少基本动画方法调用");
  }

  // 检查语法常见错误
  if (code.includes("ShowCreation")) {
    issues.push("使用了已弃用的API：ShowCreation，应替换为Create");
  }

  // 检查括号匹配
  const openParenCount = (code.match(/\(/g) || []).length;
  const closeParenCount = (code.match(/\)/g) || []).length;
  if (openParenCount !== closeParenCount) {
    issues.push("括号不匹配，可能导致语法错误");
  }

  // 检查引号匹配
  const singleQuoteCount = (code.match(/'/g) || []).length;
  const doubleQuoteCount = (code.match(/"/g) || []).length;
  if (singleQuoteCount % 2 !== 0 || doubleQuoteCount % 2 !== 0) {
    issues.push("引号不匹配，可能导致语法错误");
  }

  // LaTeX 文本模式下划线检查
  const textUnderscorePattern = /\\text\{[^}]*_[^}]*\}/;
  if (textUnderscorePattern.test(code)) {
    issues.push(
      "LaTeX 文本模式 \\text{...} 中包含下划线 _，请写作 \\\\_ 或将下标放入数学模式 $...$ 中（使用 _{...}）。"
    );
  }

  // 检查3D场景特有的设置
  if (is3D) {
    if (!code.includes("set_camera_orientation")) {
      issues.push("3D场景缺少相机角度设置");
    }

    if (
      !code.includes("add_fixed_in_frame_mobjects") &&
      (code.includes("Text(") || code.includes("MathTex("))
    ) {
      issues.push("3D场景中的2D文本应使用add_fixed_in_frame_mobjects");
    }
  }

  // 布局与清屏启发式检查
  const hasArrange = /\.arrange\s*\(/.test(code) || /next_to\s*\(/.test(code);
  const hasEdgePadding =
    /to_edge\s*\(.*buff\s*=\s*0\.[8-9]/.test(code) ||
    /to_edge\s*\(\s*(LEFT|RIGHT|UP|DOWN)\s*\)/.test(code);
  const usesStepGroup = /step_group\s*=\s*Group\s*\(\s*\)/.test(code);
  const hasFadeOutOrUncreate =
    /FadeOut\s*\(/.test(code) || /Uncreate\s*\(/.test(code);

  if (!hasArrange) {
    issues.push(
      "可能缺少排版安排：建议使用 Group(...).arrange(...) 或 next_to(..., buff=0.6+)"
    );
  }
  if (!hasEdgePadding) {
    issues.push(
      "可能缺少边界留白：建议使用 to_edge(..., buff=0.8) 或合理 shift/scale"
    );
  }
  if (!hasFadeOutOrUncreate) {
    issues.push(
      "可能缺少步骤清理：建议在步骤结束使用 FadeOut(...) 或 Uncreate(...)"
    );
  }
  if (!usesStepGroup) {
    issues.push(
      "建议采用步骤化清屏协议：使用 step_group = Group() 收纳并在步骤结束统一清理"
    );
  }

  // 返回验证结果
  return {
    valid: issues.length === 0,
    issues,
    is3D,
  };
};

/**
 * 布局问题识别（启发式）
 * @param {Array} issues - 验证结果中的问题列表
 * @returns {boolean} 是否包含布局问题
 */
const containsLayoutIssues = (issues) => {
  if (!Array.isArray(issues)) return false;
  const keywords = [
    "排版",
    "留白",
    "清理",
    "step_group",
    "步骤",
    "FadeOut",
    "Uncreate",
    "重叠",
    "越界",
  ];
  return issues.some((msg) => keywords.some((k) => String(msg).includes(k)));
};

/**
 * 自动修复Manim代码：基于现有修复接口二次生成
 * @param {string} originalCode - 原始代码
 * @param {Array|string} issues - 需要修复的问题
 * @returns {Promise<string>} 修复后的代码
 */
const autoFixManimCode = async (originalCode, issues) => {
  const synthesizedError = `自动修复：请严格落实"布局安全协议/步骤化清屏协议"。需修复的问题：${
    Array.isArray(issues) ? issues.join("；") : issues
  }`;
  return new Promise((resolve, reject) => {
    // 复用修复通道，但只取完成回调的代码
    streamFixManimCode(
      originalCode,
      synthesizedError,
      null,
      (fixedCode /*, fixIssues */) => {
        resolve(fixedCode);
      },
      (err) => reject(new Error(err))
    );
  });
};

/**
 * 发送生成Manim代码的请求
 * @param {string} prompt - 用户输入的动画描述提示词
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @param {function} onComplete - 当完成整个响应时的回调
 * @param {function} onError - 发生错误时的回调
 * @param {File|File[]} imageFiles - 可选的图片文件（最多3张）
 * @param {boolean} voiceover - 是否添加语音旁白
 * @param {string} voiceId - 选择的音色ID
 * @param {boolean} freeMode - 是否使用免费模型与免费渠道
 * @param {string} targetLang - 选定语言代码 (如 en, ja, fr 等)
 * @param {string} mode - 请求模式 ("manim" 或 "manimgx")
 */
export const streamManimCode = async (
  prompt,
  onChunkReceived,
  onComplete,
  onError,
  imageFiles,
  voiceover = false,
  voiceId = null,
  freeMode = false,
  targetLang = null,
  mode = "manim"
) => {
  const imageFilesArr = Array.isArray(imageFiles) ? imageFiles : (imageFiles ? [imageFiles] : []);
  Logger.info("开始API调用", {
    endpoint: `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    prompt,
    imageCount: imageFilesArr.length,
    targetLang,
    mode,
  });

  // 将API请求包装到队列中执行
  try {
    await requestManager.enqueue(async () => {
      try {
        // 构建简化的请求体 - 系统提示词和模型参数已移至后端
        const requestBody = {
          mode: mode || "manim", // 请求模式，用于后端选择系统提示词和模型参数
          prompt: prompt, // 用户的请求内容
          stream: false, // 改为非流式响应
          voiceover: voiceover, // 是否生成带语音旁白的代码
          voiceId: voiceId, // 选择的音色
          free_mode: freeMode,
        };
        if (targetLang) {
          requestBody.target_lang = targetLang;
        }
        const slug = getCustomClientSlug();
        if (slug) requestBody.custom_client_slug = slug;

        // 如果有图片，将所有图片转为 base64 数组
        if (imageFilesArr.length > 0) {
          const base64Images = await Promise.all(imageFilesArr.map(convertImageToBase64));
          requestBody.images = base64Images;
        }

        Logger.debug("发送API请求", requestBody);

        // 添加用户ID
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

        Logger.debug("API响应完成", { responseLength: fullResponse.length });

        // 提取代码部分
        let code = await extractManimCode(fullResponse, mode);
        // 自动转义 LaTeX 文本中未转义的下划线
        code = escapeUnderscoreInLaTeXText(code);

        Logger.info("API调用完成，返回代码", {
          responseLength: fullResponse.length,
          codeLength: code.length,
        });
        onComplete(code, chargeSession);

        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, imageCount: imageFilesArr.length },
          "success",
          { codeLength: code.length }
        );
      } catch (error) {
        Logger.error("API调用失败", error);
        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { prompt, imageCount: imageFilesArr.length },
          "error",
          { error: error.message }
        );
        const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : (error?.message || "生成失败");
        onError(msg);
      }
    });
  } catch (error) {
    Logger.error("请求队列处理失败", error);
    const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : `请求队列处理失败: ${error?.message || error}`;
    onError(msg);
  }
};

/**
 * 发送修复代码的请求，提供错误信息和原始代码
 * @param {string} originalCode - 原始Manim代码
 * @param {string} errorMessage - 渲染错误信息
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @param {function} onComplete - 当完成整个响应时的回调
 * @param {function} onError - 发生错误时的回调
 */
export const streamFixManimCode = async (
  originalCode,
  errorMessage,
  onChunkReceived,
  onComplete,
  onError
) => {
  Logger.info("开始修复代码API调用", {
    endpoint: `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    errorMessage,
  });

  // 将API请求包装到队列中执行
  try {
    await requestManager.enqueue(async () => {
      try {
        // 构建简化的请求体 - 系统提示词和模型参数已移至后端
        const requestBody = {
          mode: "code_fix", // 代码修复模式
          originalCode: originalCode, // 需要修复的代码
          errorMessage: errorMessage, // 错误信息
          stream: false, // 改为非流式响应
        };

        Logger.debug("发送修复代码API请求", requestBody);

        // 添加用户ID
        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);
        
        Logger.debug("修复代码API响应完成", {
          responseLength: fullResponse.length,
        });

        // 提取代码部分
        const engineMode = /^\s*(?:from\s+manimgx\b|import\s+manimgx\b)/m.test(originalCode) ? "manimgx" : "manim";
        const fixedCode = await extractManimCode(fullResponse, engineMode);

        // 验证修复后的代码
        const codeValidation = validateManimCode(fixedCode);
        if (!codeValidation.valid) {
          Logger.warn("修复后的代码可能仍存在问题", codeValidation.issues);
          // 即使有问题也返回修复后的代码，让用户决定是否使用
          onComplete(fixedCode, codeValidation.issues);
        } else {
          Logger.info("代码修复完成，验证通过", {
            responseLength: fullResponse.length,
            codeLength: fixedCode.length,
          });
          onComplete(fixedCode);
        }

        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { action: "fix_code" },
          "success",
          { codeLength: fixedCode.length }
        );
      } catch (error) {
        Logger.error("修复代码API调用失败", error);
        Logger.logApiCall(
          `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
          { action: "fix_code" },
          "error",
          { error: error.message }
        );
        onError(error.message);
      }
    });
  } catch (error) {
    Logger.error("修复代码请求队列处理失败", error);
    onError(`请求队列处理失败: ${error.message}`);
  }
};

/**
 * 发送二次编辑微调请求，基于原有代码按用户指令进行修改
 * @param {string} originalCode - 原始代码
 * @param {string} userInstruction - 用户的微调指令
 * @param {string} originalMode - 原始模式（manim, threejs, geogebra, qq等）
 * @param {Array} conversationHistory - 多轮对话历史 [{role, content}]
 * @param {function} onChunkReceived - 接收到流式文本块时的回调
 * @param {function} onComplete - 完成时的回调，参数为修改后的代码
 * @param {function} onError - 发生错误时的回调
 */
export const streamRefineCode = async (
  originalCode,
  userInstruction,
  originalMode,
  conversationHistory,
  onChunkReceived,
  onComplete,
  onError,
  imageFile = null,
  renderError = null  // 上一次渲染失败的真实 manim 报错，供 AI 同步修复
) => {
  Logger.info("开始二次编辑微调API调用", {
    endpoint: `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    originalMode,
    instructionLength: userInstruction?.length,
    hasImage: !!imageFile,
  });

  try {
    await requestManager.enqueue(async () => {
      try {
        const requestBody = {
          mode: "refine",
          prompt: userInstruction,
          originalCode: originalCode,
          originalMode: originalMode,
          conversation_history: conversationHistory || [],
          stream: false,
        };
        // 如果上一次渲染有报错，附带给后端让 AI 同步修复
        if (renderError) {
          requestBody.renderError = renderError;
        }
        const slug = getCustomClientSlug();
        if (slug) requestBody.custom_client_slug = slug;

        if (imageFile) {
          const base64Image = await convertImageToBase64(imageFile);
          requestBody.image = base64Image;
        }

        const currentUser = getCurrentUser();
        requestBody.userId = currentUser?.id || 0;

        Logger.debug("发送微调API请求", {
          mode: requestBody.mode,
          originalMode,
          historyLength: conversationHistory?.length,
          hasImage: !!imageFile,
        });

        const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

        Logger.debug("微调API响应完成", { responseLength: fullResponse.length });

        // 根据原始模式提取对应格式的代码
        let refinedCode;
        if (originalMode === "manim" || originalMode === "manimgx" || originalMode === "solve") {
          refinedCode = await extractManimCode(fullResponse, originalMode);
          refinedCode = escapeUnderscoreInLaTeXText(refinedCode);
        } else {
          // threejs / qq / geogebra：提取代码块或直接返回
          const codeBlockRegex = /```(?:html|javascript|js)?([\s\S]*?)```/g;
          const matches = [...fullResponse.matchAll(codeBlockRegex)];
          if (matches.length > 0) {
            // 选取最长的代码块
            refinedCode = matches.reduce((longest, match) => {
              const block = match[1].trim();
              return block.length > longest.length ? block : longest;
            }, "");
          } else {
            refinedCode = fullResponse.trim();
          }
        }

        Logger.info("微调代码生成完成", {
          responseLength: fullResponse.length,
          codeLength: refinedCode.length,
        });

        onComplete(refinedCode, fullResponse, chargeSession);
      } catch (error) {
        Logger.error("微调API调用失败", error);
        onError(error.message);
      }
    });
  } catch (error) {
    Logger.error("微调请求队列处理失败", error);
    onError(`请求队列处理失败: ${error.message}`);
  }
};

/**
 * 提交片段微调渲染请求
 *
 * 只重渲指定 scene（target_scene），然后与原始任务其余片段重新拼接。
 * SSE 进度监听复用已有的 /render_events/{id} 接口。
 *
 * @param {string} baseRequestId  - 原始批量渲染任务 ID（提供 scene_names + segment_files）
 * @param {string} targetScene    - 要重渲的场景名称
 * @param {string} code           - 新的完整 Manim 代码
 * @param {string} quality        - 渲染质量（默认 "m"）
 * @param {object} metadata       - 元数据（title, prompt, created_by 等，可选）
 * @returns {Promise<{ request_id: string }>}
 */
export const renderManimPatch = async (
  baseRequestId,
  targetScene,
  code,
  quality = "m",
  metadata = {}
) => {
  const RENDER_SERVER_URL = process.env.REACT_APP_RENDER_SERVER_URL;
  if (!RENDER_SERVER_URL) {
    throw new Error("REACT_APP_RENDER_SERVER_URL 未配置");
  }

  const body = {
    code,
    quality,
    base_request_id: baseRequestId,
    target_scene: targetScene,
    ...metadata,
  };

  const resp = await fetch(`${RENDER_SERVER_URL.replace(/\/$/, "")}/render_patch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    let errMsg = `HTTP ${resp.status}`;
    try {
      const errData = await resp.json();
      errMsg = errData?.error || errMsg;
    } catch (_) {}
    throw new Error(errMsg);
  }

  return await resp.json();
};

export { validateManimCode };

export default {
  streamManimCode,
  streamFixManimCode,
  streamRefineCode,
  extractManimCode,
  escapeUnderscoreInLaTeXText,
  validateManimCode,
  renderManimPatch,
};
